import { useEffect, useRef } from 'react';

interface World {
  angle: number;
  radius: number;
  speed: number;
  size: number;
  color: string;
  emitCooldown: number;
}

interface Signal {
  world: World;
  startX: number;
  startY: number;
  progress: number;
  speed: number;
  curve: number;
}

interface Star {
  x: number;
  y: number;
  r: number;
  alpha: number;
  twinkle: number;
}

interface Shockwave {
  r: number;
  alpha: number;
}

interface Spark {
  angle: number;
  radius: number;
  speed: number;
  size: number;
}

function hexA(hex: string, alpha: number) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function UniverseNetwork() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const palette = [
      '#5b9cff',
      '#22e0ff',
      '#7aaaff',
      '#00c8ff',
      '#8cb4ff',
      '#4ae6c4',
      '#63d7ff',
      '#a0c8ff',
      '#2ee8c8',
    ];

    let worlds: World[] = [];
    let signals: Signal[] = [];
    let stars: Star[] = [];
    let shockwaves: Shockwave[] = [];
    let sparks: Spark[] = [];
    let serverPulse = 0;
    let time = 0;

    const cx = () => width / 2;
    const cy = () => height / 2;

    function initScene() {
      const count = 9;
      worlds = [];
      const maxR = Math.min(width, height) * 0.46;
      for (let i = 0; i < count; i++) {
        worlds.push({
          angle: (i / count) * Math.PI * 2 + Math.random() * 0.5,
          radius: maxR * (0.38 + Math.random() * 0.6),
          speed: (0.0005 + Math.random() * 0.0009) * (Math.random() > 0.5 ? 1 : -1),
          size: 3.5 + Math.random() * 6,
          color: palette[i % palette.length],
          emitCooldown: Math.random() * 60,
        });
      }

      const starCount = Math.floor((width * height) / 9000);
      stars = [];
      for (let i = 0; i < starCount; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 1.4 + 0.3,
          alpha: Math.random() * 0.5 + 0.1,
          twinkle: Math.random() * Math.PI * 2,
        });
      }

      // Sparks orbiting the central core
      sparks = [];
      const sparkCount = 14;
      for (let i = 0; i < sparkCount; i++) {
        sparks.push({
          angle: Math.random() * Math.PI * 2,
          radius: 14 + Math.random() * 26,
          speed: (0.01 + Math.random() * 0.02) * (Math.random() > 0.5 ? 1 : -1),
          size: 1 + Math.random() * 1.8,
        });
      }
    }

    function resize() {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initScene();
    }

    function worldPos(w: World) {
      const x = cx() + Math.cos(w.angle) * w.radius;
      const y = cy() + Math.sin(w.angle) * w.radius * 0.62;
      return { x, y };
    }

    function draw() {
      ctx.clearRect(0, 0, width, height);

      // Background stars
      for (const s of stars) {
        const a = s.alpha * (0.6 + 0.4 * Math.sin(time * 0.02 + s.twinkle));
        ctx.globalAlpha = a;
        ctx.fillStyle = '#cfe0ff';
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      const c = { x: cx(), y: cy() };

      // Update worlds + emit signals (people)
      for (const w of worlds) {
        w.angle += w.speed * 16;
        w.emitCooldown -= 1;
        if (w.emitCooldown <= 0 && signals.length < 90) {
          const p = worldPos(w);
          signals.push({
            world: w,
            startX: p.x,
            startY: p.y,
            progress: 0,
            speed: 0.008 + Math.random() * 0.014,
            curve: (Math.random() - 0.5) * 90,
          });
          w.emitCooldown = 35 + Math.random() * 90;
        }
      }

      // Connection lines from each world to the central server
      for (const w of worlds) {
        const p = worldPos(w);
        ctx.beginPath();
        ctx.moveTo(c.x, c.y);
        ctx.lineTo(p.x, p.y);
        ctx.strokeStyle = 'rgba(120, 170, 255, 0.10)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Worlds
      for (const w of worlds) {
        const p = worldPos(w);
        const pulse = 0.6 + 0.4 * Math.sin(time * 0.03 + w.angle);

        // Glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, w.size * 4);
        grad.addColorStop(0, hexA(w.color, 0.35));
        grad.addColorStop(1, hexA(w.color, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, w.size * 4, 0, Math.PI * 2);
        ctx.fill();

        // Orbit ring
        ctx.beginPath();
        ctx.ellipse(c.x, c.y, w.radius, w.radius * 0.62, 0, 0, Math.PI * 2);
        ctx.strokeStyle = hexA(w.color, 0.12);
        ctx.lineWidth = 1;
        ctx.stroke();

        // Planet core
        ctx.beginPath();
        ctx.arc(p.x, p.y, w.size * pulse, 0, Math.PI * 2);
        ctx.fillStyle = w.color;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(p.x, p.y, w.size * pulse * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.fill();
      }

      // Signals (people traveling to the server)
      for (let i = signals.length - 1; i >= 0; i--) {
        const s = signals[i];
        s.progress += s.speed;
        if (s.progress >= 1) {
          signals.splice(i, 1);
          serverPulse = 1;
          shockwaves.push({ r: 8, alpha: 0.7 });
          continue;
        }
        const t = s.progress;
        const mx = (s.startX + c.x) / 2 + s.curve * (1 - Math.abs(0.5 - t) * 2);
        const my = (s.startY + c.y) / 2;
        const x = (1 - t) * (1 - t) * s.startX + 2 * (1 - t) * t * mx + t * t * c.x;
        const y = (1 - t) * (1 - t) * s.startY + 2 * (1 - t) * t * my + t * t * c.y;

        // Trail glow
        ctx.beginPath();
        ctx.arc(x, y, 5, 0, Math.PI * 2);
        ctx.fillStyle = hexA(s.world.color, 0.25);
        ctx.fill();
        // Person core
        ctx.beginPath();
        ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
        ctx.fill();
      }

      // --- Central server (spectacular) ---
      serverPulse *= 0.94;
      const coreR = 8 + serverPulse * 34;

      // Outer ambient halo
      const serverGrad = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, 110);
      serverGrad.addColorStop(0, 'rgba(91, 156, 255, 0.60)');
      serverGrad.addColorStop(0.3, 'rgba(34, 224, 255, 0.28)');
      serverGrad.addColorStop(0.6, 'rgba(34, 224, 255, 0.10)');
      serverGrad.addColorStop(1, 'rgba(34, 224, 255, 0)');
      ctx.fillStyle = serverGrad;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 110, 0, Math.PI * 2);
      ctx.fill();

      // Rotating energy ring (dashed)
      ctx.save();
      ctx.translate(c.x, c.y);
      ctx.rotate(time * 0.01);
      ctx.setLineDash([6, 8]);
      ctx.strokeStyle = 'rgba(34, 224, 255, 0.45)';
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.arc(0, 0, 26, 0, Math.PI * 2);
      ctx.stroke();
      ctx.rotate(-time * 0.018);
      ctx.setLineDash([3, 9]);
      ctx.strokeStyle = 'rgba(160, 200, 255, 0.35)';
      ctx.beginPath();
      ctx.arc(0, 0, 38, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      // Orbiting sparks around the core
      for (const sp of sparks) {
        sp.angle += sp.speed;
        const sx = c.x + Math.cos(sp.angle) * sp.radius;
        const sy = c.y + Math.sin(sp.angle) * sp.radius * 0.75;
        ctx.beginPath();
        ctx.arc(sx, sy, sp.size, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(180, 225, 255, 0.8)';
        ctx.fill();
      }

      // Pulsing ring (grows with each connection)
      ctx.beginPath();
      ctx.arc(c.x, c.y, coreR, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(34, 224, 255, ${0.55 * (0.3 + 0.7 * serverPulse)})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Expanding shockwaves (emitted on each arrival)
      for (let i = shockwaves.length - 1; i >= 0; i--) {
        const sw = shockwaves[i];
        sw.r += 1.8;
        sw.alpha *= 0.96;
        if (sw.alpha < 0.02 || sw.r > 120) {
          shockwaves.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.arc(c.x, c.y, sw.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(34, 224, 255, ${sw.alpha})`;
        ctx.lineWidth = 1.4;
        ctx.stroke();
      }

      // Inner glow core
      const coreGrad = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, 16);
      coreGrad.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
      coreGrad.addColorStop(0.35, 'rgba(160, 220, 255, 0.7)');
      coreGrad.addColorStop(1, 'rgba(34, 224, 255, 0)');
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 16, 0, Math.PI * 2);
      ctx.fill();

      // White hot center
      ctx.beginPath();
      ctx.arc(c.x, c.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      time++;
      raf = requestAnimationFrame(draw);
    }

    resize();
    raf = requestAnimationFrame(draw);

    const ro = new ResizeObserver(() => resize());
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none -z-10"
      aria-hidden="true"
    />
  );
}

export default UniverseNetwork;